"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight, Menu, X } from "lucide-react";
import { Mark } from "./primitives";
import ThemeToggle from "./ThemeToggle";
import { cn } from "@/lib/utils";

const LINKS = [
  ["/#work", "Work"],
  ["/#services", "Services"],
  ["/case-studies", "Case studies"],
  ["/#pricing", "Pricing"],
  ["/blog", "Blog"],
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu after navigating.
  useEffect(() => setOpen(false), [pathname]);

  const isActive = (href) => !href.startsWith("/#") && pathname.startsWith(href);

  return (
    <div className="sticky top-0 z-50 px-4 pt-3">
      <header
        className={cn(
          "mx-auto flex max-w-6xl items-center gap-6 rounded-full border border-border bg-background/85 pr-2 pl-5 backdrop-blur-md transition-all duration-300",
          scrolled || open ? "h-14 shadow-float" : "h-16"
        )}
      >
        <Link href="/" aria-label="motion73 home" className="flex items-center gap-2.5">
          <Mark size={30} />
          <span className="font-display text-lg font-bold">motion73</span>
        </Link>

        <nav aria-label="Main" className="mx-auto hidden items-center gap-7 text-[15px] lg:flex">
          {LINKS.map(([href, label]) => (
            <Link
              key={href}
              href={href}
              aria-current={isActive(href) ? "page" : undefined}
              className={cn("group relative py-1 transition-colors hover:text-foreground", isActive(href) ? "text-foreground" : "text-muted-foreground")}
            >
              {label}
              <span className={cn("absolute inset-x-0 -bottom-0.5 h-px origin-left bg-current transition-transform duration-300 group-hover:scale-x-100", isActive(href) ? "scale-x-100" : "scale-x-0")} />
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <ThemeToggle />
          <Link
            href="/#contact"
            className="group hidden h-10 items-center gap-2 rounded-full bg-cta px-5 text-sm font-semibold whitespace-nowrap text-cta-foreground transition-all duration-300 hover:-translate-y-0.5 hover:shadow-cta sm:inline-flex"
          >
            Start your website
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-[3px]" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            className="flex size-10 items-center justify-center rounded-full border border-border lg:hidden"
          >
            {open ? <X className="size-[18px]" aria-hidden="true" /> : <Menu className="size-[18px]" aria-hidden="true" />}
          </button>
        </div>
      </header>

      {open && (
        <nav id="mobile-menu" aria-label="Mobile" className="mx-auto mt-2 max-w-6xl rounded-3xl border border-border bg-background p-3 shadow-float motion-safe:animate-rise lg:hidden">
          {LINKS.map(([href, label]) => (
            <Link key={href} href={href} onClick={() => setOpen(false)} className="block rounded-2xl px-4 py-3 text-base font-medium hover:bg-secondary">
              {label}
            </Link>
          ))}
          <Link
            href="/#contact"
            onClick={() => setOpen(false)}
            className="mt-2 flex h-12 items-center justify-center gap-2 rounded-2xl bg-cta font-semibold text-cta-foreground"
          >
            Start your website
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </nav>
      )}
    </div>
  );
}
